import React from "react";
import styled, { css } from "styled-components";

import FilterChip from "../../../../components/filter-chip/FilterChip";
import {
  projectBannerAccent,
  projectBannerBackground,
  projectBannerTextColor,
} from "../../../taskboard/components/projectBannerColor";

export interface ProjectChipOption {
  id: string;
  name: string;
  /** プロジェクトバナーのカラー番号（"1"〜"18"） */
  bannerColor?: string | null;
}

interface ProjectFilterChipsProps {
  /** 選択肢（リスト表示と同じ並び順で渡す） */
  projects: ProjectChipOption[];
  /** 選択中のプロジェクト ID（複数可）。空 = 全プロジェクト */
  selected: string[];
  // eslint-disable-next-line no-unused-vars
  onToggle: (projectId: string) => void;
  onClear: () => void;
}

/**
 * プロジェクトチップ。FilterChip と同じ pill 形状・寸法で、配色だけ
 * プロジェクトバナー由来にする（選択中はバナー背景そのまま =
 * グラデーションも再現、未選択は代表色の枠 + 文字）。
 * FilterChip の `color` prop は単色前提のため、グラデーション対応の
 * 専用チップとしてここで持つ。
 */
const BannerChip = styled.button.attrs({ className: "bodytext-13" })<{
  $selected: boolean;
  $background: string;
  $accent: string;
  $text: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid ${({ $accent }) => $accent};
  padding: 4px 8px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  appearance: none;
  margin: 0;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;

  ${({ $selected, $background, $accent, $text }) =>
    $selected
      ? css`
          background: ${$background};
          color: ${$text};

          &:hover {
            box-shadow: 2px 4px 8px 3px rgba(0, 0, 0, 0.15);
          }
        `
      : css`
          background: #fff;
          color: ${$accent};

          /* 未選択ホバー: クリックでバナー色になる予告 + 影 */
          &:hover {
            background: ${$background};
            color: ${$text};
            box-shadow: 2px 4px 8px 3px rgba(0, 0, 0, 0.15);
          }
        `}

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

/**
 * タスクボードのプロジェクトフィルターチップ。
 * 先頭の「すべて」（既定）+ プロジェクトのコードネームを並べる。
 * 担当者チップと同じ**複数選択**で、クリックで選択に追加 / 再クリックで解除、
 * 「すべて」で全解除する（何も選んでいない = 全プロジェクト表示）。
 * 各プロジェクトチップはバナーカラーで塗り分ける（選択中はバナー背景を
 * そのまま適用）。並び順は呼び出し側（リスト表示と同じ最終期日順）に従う。
 */
const ProjectFilterChips: React.FC<ProjectFilterChipsProps> = ({
  projects,
  selected,
  onToggle,
  onClear,
}) => (
  <div
    className="u-row u-items-center u-wrap u-gap8"
    role="group"
    aria-label="プロジェクトで絞り込み"
  >
    <FilterChip selected={selected.length === 0} onClick={onClear}>
      すべて
    </FilterChip>
    {projects.map((project) => (
      <BannerChip
        key={project.id}
        type="button"
        aria-pressed={selected.includes(project.id)}
        $selected={selected.includes(project.id)}
        $background={projectBannerBackground(project.bannerColor)}
        $accent={projectBannerAccent(project.bannerColor)}
        $text={projectBannerTextColor(project.bannerColor)}
        onClick={() => onToggle(project.id)}
      >
        {project.name}
      </BannerChip>
    ))}
  </div>
);

export default ProjectFilterChips;
